import { useTranslation } from 'react-i18next';
import type { Control } from 'react-hook-form';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { WorkScheduleSegmentsField } from './WorkScheduleSegmentsField';
import type { MemberWorkScheduleFormInput } from '../schemas';

interface MemberWorkScheduleDialogBodyProps {
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  hasOverride: boolean;
  defaultHours?: number;
  control: Control<MemberWorkScheduleFormInput>;
  onSubmit: () => void;
}

/**
 * Body of `MemberWorkScheduleDialog` — renders the loading / error / form state.
 * Extracted to keep the dialog component within the size budget; the `<form>` id
 * is referenced by the footer submit button that lives in the parent.
 */
export function MemberWorkScheduleDialogBody({
  isFetching,
  isError,
  onRetry,
  hasOverride,
  defaultHours,
  control,
  onSubmit,
}: MemberWorkScheduleDialogBodyProps) {
  const { t } = useTranslation('workSchedules');

  if (isFetching) {
    return (
      <div className="flex flex-col gap-3 py-2" data-testid="work-schedules-member-loading">
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid="work-schedules-member-error"
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-4"
      >
        <p className="text-sm text-destructive">{t('member.loadError')}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          {t('member.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <form id="member-work-schedule-form" onSubmit={onSubmit} className="flex flex-col gap-4 py-2">
      {!hasOverride && defaultHours !== undefined && (
        <p className="text-xs text-muted-foreground" data-testid="work-schedules-member-inherits">
          {t('member.inheritsDefault', { hours: defaultHours })}
        </p>
      )}
      <WorkScheduleSegmentsField control={control} />
    </form>
  );
}
